import React from "react";
import styled from "styled-components";
import ArbonicsDialog from "../comps/ArbonicsDialog/ArbonicsDialog";
import { sendEvent } from "../../../api";
import LockedInfoBanner from "./components/LockedInfoBanner";
import { Input, Text, Icon, Button } from "@virkesborsen/ui-component-library";
import { TailwindWrapper } from "../../TailwindWrapper/TailwindWrapper";
import { ErrorModal } from "../../../modals";
import { useMetricData } from "./hooks/UseMetricData";

import Skeleton from "../comps/Skeleton";
import WidgetWrapper from "./components/WidgetWrapper";
import PropSelector from "../comps/PropSelector";

import LineChartWidget from "./widgets/LineChartWidget";
import TableWidget from "./widgets/TableWidget";
import MetricAreaWidget from "./widgets/MetricAreaWidget";
import MetricVolumeWidget from "./widgets/MetricVolumeWidget";
import BarChartWidget from "./widgets/BarGraphWidget";
import KeyDataWidget from "./widgets/KeyDataWidget";
import OpportunitiesWidget from "./widgets/OpportunitiesWidget";

const PropselectorContainer = styled.div`
  width: 90vw;
  max-width: 450px;
  top: 10px;
`;

export const baseSkeletonSetup = () => {
  return (
    <div className="flex flex-col space-y-3 p-4">
      <Skeleton className="h-[125px] w-full rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[200px] max-w-full" />
        <Skeleton className="h-4 w-[250px] max-w-full mb-6" />
        <Skeleton className="h-4 w-[200px] max-w-full" />
        <Skeleton className="h-4 w-[250px] max-w-full" />
        <Skeleton className="h-4 w-[225px] max-w-full" />
      </div>
    </div>
  );
};

export default function Overblick() {
  const {
    loading,
    totalVolume,
    avgGrowth,
    updateTotalVolume,
    updateAvgGrowth,
    totalActiveProps,
    gotoSbpPdf,
  } = useMetricData();

  const [isArbonicsDialogVisible, setIsArbonicsDialogVisible] =
    React.useState(false);
  const [errorModal, setErrorModal] = React.useState(false);
  const [editData, setEditData] = React.useState({
    totalVolume: undefined,
    avgGrowth: undefined,
  });
  const tableWidgetRef = React.useRef(null);

  const noActiveProps = !loading && totalActiveProps === 0;
  

  React.useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem(
      "overblickScrollPosition"
    );
    if (loading || noActiveProps || !savedScrollPosition) return;

    const position = parseInt(savedScrollPosition, 10);
    sessionStorage.removeItem("overblickScrollPosition");

    setTimeout(() => {
      const mainContainer = document.getElementById("mainContainer");
      if (mainContainer) {
        mainContainer.scrollTo({
          top: position,
          behavior: "smooth",
        });
      }
    }, 300);
  }, [loading, noActiveProps]);

  const formatValueForDisplay = (value, field) => {
    if (value === null || value === undefined || typeof value !== "number") {
      return "";
    }

    if (field === "avgGrowth") {
      return value.toFixed(1).replace(".", ",");
    }
    return Math.round(value).toString();
  };

  const handleInputChange = (field) => (value) => {
    let sanitized = String(value);

    if (field === "avgGrowth") {
      // For avgGrowth we allow comma for decimal
      sanitized = sanitized.replace(".", ",");
    } else {
      // For totalVolume we remove any decimal separators
      sanitized = sanitized.replace(/[.,]/g, "");
    }

    setEditData({ ...editData, [field]: sanitized });
  };

  const normalizeValue = (value, fallback) => {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    const normalized = String(value).replace(",", ".");
    return parseFloat(normalized);
  };

  const handleArbonicsClick = () => {
    setIsArbonicsDialogVisible(true);
    sendEvent("co2_open_modal", { source: "overblick" });
    gtag("event", "co2_open_modal", { method: "site" });
  };

  const editKeyValueContent = {
    title: "Ändra",
    submitLabel: "Spara",
    content: (isLoading) => (
      <>
        <div className="mb-2">
          <Input
            tw
            debounceDelay={0}
            variant="fluid"
            className="w-full"
            label="Totalvolym"
            value={
              editData?.totalVolume ??
              formatValueForDisplay(totalVolume, "totalVolume")
            }
            onChange={handleInputChange("totalVolume")}
            suffix="m³sk"
            disabled={isLoading}
          />
        </div>
        <div>
          <Input
            tw
            debounceDelay={0}
            variant="fluid"
            className="w-full"
            label="Medelbonitet"
            value={
              editData?.avgGrowth ??
              formatValueForDisplay(avgGrowth, "avgGrowth")
            }
            onChange={handleInputChange("avgGrowth")}
            suffix="m³sk/ha/år"
            disabled={isLoading}
          />
        </div>
      </>
    ),
    onSubmit: async () => {
      const avgGrowthValue = normalizeValue(editData.avgGrowth, avgGrowth);
      const totalVolumeValue = normalizeValue(
        editData.totalVolume,
        totalVolume
      );

      await Promise.all([
        updateAvgGrowth(avgGrowthValue),
        updateTotalVolume(totalVolumeValue),
      ]);
    },
  };

  const editVolumeContent = {
    title: "Ändra",
    submitLabel: "Spara",
    content: (isLoading) => (
      <div className="mb-2">
        <Input
          tw
          debounceDelay={0}
          variant="fluid"
          className="w-full"
          label="Totalvolym"
          value={
            editData?.totalVolume ??
            formatValueForDisplay(totalVolume, "totalVolume")
          }
          onChange={handleInputChange("totalVolume")}
          suffix="m³sk"
          disabled={isLoading}
        />
      </div>
    ),
    onSubmit: async () => {
      const totalVolumeValue = normalizeValue(
        editData.totalVolume,
        totalVolume
      );
      await Promise.all([updateTotalVolume(totalVolumeValue)]);
    },
  };

  if (noActiveProps) {
    return (
      <div
        className="position-relative bg-cloud-50"
        style={{ minHeight: "100dvh" }}
      >
        <PropselectorContainer className="sticky-top z-1040 mx-auto">
          <PropSelector />
        </PropselectorContainer>

        <TailwindWrapper>
          <div className="flex flex-col items-center mt-12 h-[400px]">
            <Text variant="text-xl/leading-none/bold">
              Oj oj, inga aktiva fastigheter hittades
            </Text>
            <div className="flex flex-row items-center gap-2 mt-4">
              <Text variant="text-sm/leading-normal/normal">
                Vänligen välj en fastighet i menyn här ovanför
              </Text>
              <Icon
                tw
                name="ArrowRightSimple"
                size="large"
                className="rotate-[-90deg]"
              />
            </div>
          </div>
        </TailwindWrapper>
      </div>
    );
  }

  return (
    <div
      className="position-relative bg-cloud-50"
      style={{ minHeight: "100dvh" }}
    >
      <PropselectorContainer className="sticky-top z-1040 mx-auto">
        <PropSelector />
      </PropselectorContainer>

      <TailwindWrapper>
        <div className="pt-[80px]">
          {window.location.pathname.includes('sudo') &&  <div className="sticky top-0 z-1040 sm:ml-8 lg:ml-52 mt-4"><Button tw variant="primary" onClick={(e) => gotoSbpPdf(e)}>Hämta Pdf</Button></div>}
          <LockedInfoBanner className="max-w-[100%] lg:max-w-[100%] xl:max-w-[1240px]" />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 auto-rows-[434px] gap-4 max-w-[1240px] px-8 pt-4 mx-auto mb-8">
            <WidgetWrapper
              title="Areal"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Areal avser den sammanlagda markytan inom fastighetens
                      gränser, vanligtvis angiven i hektar (ha). I
                      skogsbruksplanen delas arealen in i olika marktyper,
                      exempelvis:
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Skogsmark:</b> mark som används för virkesproduktion
                      och har förutsättningar för trädens tillväxt.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Övrig mark:</b> mark som inte räknas som produktiv
                      skogsmark, till exempel impediment, betesmarker, vägar
                      eller bebyggelse.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Vatten:</b> sjöar, myrar eller andra vattenytor som
                      ingår i fastigheten.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Arealuppdelningen ger en grundläggande bild av
                      fastighetens sammansättning och används som utgångspunkt
                      för planering, beräkning av virkesförråd och värdering av
                      skogen.
                    </Text>
                  </div>
                </div>
              }
            >
              {loading ? (
                baseSkeletonSetup()
              ) : totalActiveProps > 0 ? (
                <MetricAreaWidget />
              ) : (
                baseSkeletonSetup()
              )}
            </WidgetWrapper>

            <WidgetWrapper
              title="Virkesförråd"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      I en skogsbruksplan avser virkesförråd den totala mängden
                      virke som finns stående i skogen vid
                      inventeringstillfället. Det uttrycks i kubikmeter skog
                      (m³sk), vilket motsvarar den fasta volymen av stamved ovan
                      stubben, alltså den del av träden som kan utnyttjas vid
                      avverkning.
                    </Text>
                  </div>

                  <Text variant="text-sm/leading-normal/normal">
                    Virkesförrådet visar hur mycket biologiskt och ekonomiskt
                    värde som för närvarande är bundet i skogen och är därmed en
                    av planens mest centrala uppgifter. Det ger en grund för att
                    bedöma avverkningspotential, tillväxt och värdeutveckling
                    över tid.
                  </Text>
                  <Text variant="text-sm/leading-normal/normal">
                    På Treebula anger vi virkesförrådet både utifrån
                    trädslagsfördelning och huggningsklasser.
                  </Text>
                </div>
              }
              {...(totalActiveProps === 1
                ? { editDialog: editVolumeContent }
                : {})}
            >
              {loading ? baseSkeletonSetup() : <MetricVolumeWidget />}
            </WidgetWrapper>
            <WidgetWrapper
              title="Nyckeldata"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Totalvolym
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Ett nyckeltal som visar <i>total volym</i> för den
                      sammanlagda mängden virke som finns i skogen, oavsett
                      trädslag eller åldersklass. Totalvolymen omfattar både
                      slutavverkningsbar skog och yngre bestånd.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Fastighetsvärde
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser ett uppskattat marknadsvärde för skogsfastigheten
                      som bygger på senaste försäljningsstatistiken från Ludvig
                      & Co och Svefa, kombinerat med fastighetens virkesförråd.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Virkesförråd medeltal
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser genomsnittlig virkesvolym per hektar produktiv
                      skogsmark inom fastigheten, uttryckt i m³sk/ha (kubikmeter
                      stammar på rot per hektar skogsmark).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Medelbonitet
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser ett mått på markens produktionsförmåga, uttryckt i
                      m³sk per hektar och år (m³sk/ha/år).
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Beräkning:
                      <br />
                      Medelboniteten beräknas som ett vägt medelvärde av
                      boniteten för fastighetens olika bestånd. Den anger hur
                      mycket virke marken i genomsnitt kan producera per hektar
                      och år under normala förhållanden.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Tillväxt
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser den årliga ökningen av virkesvolym i skogen,
                      uttryckt i m³sk per år (kubikmeter stammar på rot per år).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Värdetillväxt
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser den årliga ekonomiska tillväxten i skogens värde,
                      uttryckt i kronor per år (kr/år).
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Beräkning:
                      <br />
                      Den beräknas genom att multiplicera den årliga tillväxten
                      (m³sk/år) med det aktuella marknadspriset per m³sk,
                      baserat på senaste försäljningsstatistik från Ludvig & Co
                      och Svefa.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Årlig tillväxt
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser den årliga ökningen av virkesvolym i skogen,
                      uttryckt i m³sk per år (kubikmeter stammar på rot per år).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Potential kolkrediter
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      En preliminär analys visar om det finns möjlighet att
                      förlänga omloppstiden på din slutavverkningsmogna skog och
                      därigenom tjäna kolkrediter. En djupare analys behövs för
                      att verkligen verifiera potentialen.
                    </Text>
                  </div>
                </div>
              }
              {...(totalActiveProps === 1
                ? { editDialog: editKeyValueContent }
                : {})}
            >
              {loading ? baseSkeletonSetup() : <KeyDataWidget />}
            </WidgetWrapper>

            <WidgetWrapper
              title="Möjligheter"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Skogen rymmer många värden, både ekonomiska, miljömässiga
                      och rekreativa. Här hittar du konkreta sätt att{" "}
                      <b>utnyttja utnyttja din skogs potential</b> utifrån just
                      dina förutsättningar.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Här kan du:
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Sälja biobränsle::</b> Ta del av alternativ för
                      brännved och GROT som kan ge ytterligare intäkter.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Utforska kolkrediter:</b> Lär dig mer om hur din skog
                      kan bidra till klimatnytta och hur du kan delta i
                      kolkreditsprogram.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Beställa drönarinventering:</b> Få en detaljerad analys
                      av din skog med högupplösta bilder och identifiering av
                      eventuella skogsskador, till exempel stormfällning eller
                      insektsangrepp.
                    </Text>
                  </div>
                </div>
              }
            >
              {loading ? (
                baseSkeletonSetup()
              ) : (
                <OpportunitiesWidget showDialog={handleArbonicsClick} />
              )}
            </WidgetWrapper>

            <WidgetWrapper
              title="Fastighetsvärde utv. (kr/m³sk)"
              infoContent={
                <Text variant="text-sm/leading-normal/normal">
                  Avser ett uppskattat marknadsvärde för skogsfastigheten som
                  bygger på senaste försäljningsstatistiken från Ludvig & Co och
                  Svefa, kombinerat med fastighetens virkesförråd.
                </Text>
              }
            >
              {loading ? baseSkeletonSetup() : <LineChartWidget />}
            </WidgetWrapper>

            <WidgetWrapper
              title="Åldersfördelning"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Åldersfördelningen visar hur skogens bestånd är fördelade
                      i olika åldersintervall och presenteras i skogsbruksplanen
                      på två sätt:
                    </Text>

                    <Text variant="text-sm/leading-normal/normal">
                      <b>Arealfördelning per åldersintervall:</b> visar hur stor
                      del av den produktiva skogsmarken (i hektar) som tillhör
                      varje åldersklass. Den beskriver alltså hur mycket mark
                      som upptas av ungskog, gallringsskog och äldre skog.
                    </Text>

                    <Text variant="text-sm/leading-normal/normal">
                      <b>Volymfördelning per åldersintervall:</b> visar hur stor
                      andel av det totala virkesförrådet (i m³sk) som finns i
                      respektive åldersklass.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Skillnaden mellan areal och volym
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Skillnaden mellan dessa två sätt att visa
                      åldersfördelningen är central:
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Arealfördelningen speglar skogens struktur, hur stor del
                      av marken som befinner sig i olika utvecklingsstadier.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Volymfördelningen visar istället var värdet och
                      virkesmassan finns, eftersom äldre bestånd innehåller
                      betydligt mer virke per hektar än yngre.
                    </Text>
                  </div>
                  <Text variant="text-sm/leading-normal/normal">
                    I praktiken innebär det att unga skogar ofta upptar en
                    större andel av arealen, medan äldre bestånd dominerar
                    volymen. Tillsammans ger de två fördelningarna en
                    helhetsbild av skogens åldersstruktur, produktion och
                    framtida avverkningsmöjligheter.
                  </Text>
                </div>
              }
            >
              {loading ? baseSkeletonSetup() : <BarChartWidget />}
            </WidgetWrapper>
          </div>
        </div>

        {totalActiveProps > 0 && (
          <div
            ref={tableWidgetRef}
            className="grid gap-4 max-w-[1240px] px-8 pt-4 mx-auto mb-8"
          >
            <WidgetWrapper
              title="Avdelningsbeskrivning"
              className="col-span-1"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Avdelningsbeskrivningen innehåller detaljerad information
                      om varje enskilt bestånd (eller avdelning) inom
                      fastigheten. Varje avdelning är ett avgränsat område med
                      likartade förutsättningar, till exempel trädslag, ålder,
                      bonitet och skötseltillstånd.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      I beskrivningen anges:
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Areal:</b> avdelningens storlek i hektar.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Trädslag och ålder:</b> vilka träd som dominerar och
                      hur gammal skogen är.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Virkesförråd och tillväxt:</b> mängden stående virke
                      och hur mycket den växer per år.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Huggningsklass:</b> vilket utvecklingsstadium skogen
                      befinner sig i (t.ex. ungskog, gallringsskog,
                      slutavverkningsskog).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/medium">
                      Syfte och användning
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avdelningsbeskrivningen ger en praktisk och uppdaterad
                      bild av skogen på beståndsnivå. Den används för att:
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Planera skötselåtgärder och avverkningar i tid och rum.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Beräkna virkesförråd, tillväxt och ekonomiskt värde.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Skapa underlag för hållbart och långsiktigt brukande av
                      skogen.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Tillsammans med kartan utgör avdelningsbeskrivningen den
                      operativa delen av skogsbruksplanen, där övergripande
                      strategier omsätts i konkreta åtgärder för varje del av
                      fastigheten.
                    </Text>
                  </div>
                </div>
              }
            >
              <TableWidget />
            </WidgetWrapper>
          </div>
        )}
      </TailwindWrapper>
      <ArbonicsDialog
        visible={isArbonicsDialogVisible}
        hide={() => {
          setIsArbonicsDialogVisible(false);
        }}
      />

      {/* ERROR Modal */}
      <ErrorModal
        show={errorModal}
        title={errorModal?.title}
        message={errorModal?.message}
        onToggle={() => setErrorModal(false)}
      />
    </div>
  );
}
